import { Item, Collection, timesQty, join } from "./collection_base"
import DataLoader from "#lib/data_loader"

// Cyberware — a JSON array of { slug, cost, hc, notes } (display-focused: name/type/
// hc identity looked up by slug). Each piece answers its own Humanity Cost; the
// collection answers the always-on stat bonuses (merged per stat). computed-stats
// asks both via the cp2020--cyberware outlet.

export class CyberwareItem extends Item {
  // Fill self: stored controls (cost/hc/notes) + catalog display (name/description/
  // identity). Moved here from CyberwareCollection#fillItem — same body, on the item.
  async fill(entry) {
    const b = this.builder()
    const c = (await DataLoader.systemGet("cyberware"))[entry.slug] || {}
    b.fill(entry, { fields: ["cost", "hc", "notes"] })
    b.text("name", c.name || "(unknown)")
    b.text("description", c.description)
    b.text("identity", join(c.type, c.subtype || null, c.hc ? `HC ${c.hc}` : null))
  }

  // Blob entry from the STASH: slug + id + the stored control fields, as stashed. Catalog
  // display (name/description/identity) is in the stash too but NOT serialized (fixed list).
  entry() {
    return {
      slug: this.slugValue || "",
      id: this.get("id"),
      cost: this.get("cost"),
      hc: this.get("hc"),
      notes: this.get("notes"),
    }
  }

  cost() { return timesQty({ cost: this.get("cost"), qty: this.get("qty") }) }

  // Humanity Cost of this piece (leniently parsed; may be fractional/negative).
  humanityCost() {
    const n = Number(this.get("hc"))
    return Number.isFinite(n) ? n : 0
  }
}

export class CyberwareCollection extends Collection {
  static CATALOGS = ["cyberware", "cyberware-bonuses", "cyberware_armor"]
  static outlets = ["cp2020--armor"]

  // hc seeds from the catalog's hc_avg (a rename), then is GM-editable.
  async newEntry(slug) {
    const c = (await DataLoader.systemGet("cyberware"))[slug]
    if (!c) return null
    return { slug, cost: c.cost, hc: c.hc_avg, notes: "" }
  }

  // Adding armor-granting cyberware SPAWNS a persisted armor piece. addClient is the
  // newly-added seam (cold-load + per-save re-render both go through hydrate, never here),
  // so the spawn fires exactly ONCE per user pick — no dedup flag needed. Fire-and-forget:
  // one-way, one-time, NO back-link (removing/editing the cyberware never touches the
  // spawned armor — the user owns that bookkeeping). Capture the slug BEFORE super, which
  // clears the select value pre-await.
  async addClient(event) {
    const slug = event.target.value
    await super.addClient(event)
    if (slug) await this.spawnArmorFor(slug)
  }

  // Follow the picked cyberware's `armorGrant` -> its cyberware_armor spec -> the armor
  // collection's addEntry (a resolved, slug-less entry — same covers[] shape as armor.json).
  // No-op when the cyberware grants no armor, the spec is missing, or no armor area is on
  // the page (the outlet is optional — a sheet without an armor column still adds cyberware).
  async spawnArmorFor(slug) {
    const c = (await DataLoader.systemGet("cyberware"))[slug]
    const grant = c?.armorGrant
    if (!grant || !this.hasCp2020ArmorOutlet) return
    const spec = (await DataLoader.systemGet("cyberware_armor"))[grant]
    if (spec) this.cp2020ArmorOutlet.addEntry({ ...spec })
  }

  humanityLoss() {
    return this.getAll().reduce((sum, i) => sum + i.humanityCost(), 0)
  }

  // Always-on chrome stat bonuses, merged per stat -> { REF: 2 }. Activatable (speedware)
  // bonuses are excluded — those are show-mode toggles.
  async statBonuses() {
    const catalog = await DataLoader.systemGet("cyberware-bonuses")
    const totals = {}
    for (const item of this.getAll()) {
      const entry = catalog[item.slugValue]
      if (!entry?.bonuses) continue
      for (const b of entry.bonuses) {
        if (b.activatable || b.target?.kind !== "stat") continue
        totals[b.target.stat] = (totals[b.target.stat] || 0) + (Number(b.amount) || 0)
      }
    }
    return totals
  }
}
